import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Eye, EyeOff, KeyRound, Lock } from "lucide-react";
import Button from "../../components/ui/Button";
import { useAuth } from "../../contexts/AuthContext";

const PasswordField = ({ label, value, onChange, visible, onToggle, autoComplete }) => (
  <div className="space-y-2">
    <label className="text-sm font-medium leading-none text-foreground">{label}</label>
    <div className="relative">
      <Lock
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <input
        type={visible ? "text" : "password"}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pl-10 pr-11 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={visible ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
        aria-pressed={visible}
      >
        {visible ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
      </button>
    </div>
  </div>
);

const ChangePassword = () => {
  const { changePassword, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (form.password.length < 10) return setError("Use at least 10 characters.");
    if (form.password !== form.confirm) return setError("Passwords do not match.");

    setSaving(true);
    const result = await changePassword(form.password);
    if (result.error) {
      setError(result.error.message || "Password could not be changed.");
    } else {
      await refreshProfile();
      navigate("/");
    }
    setSaving(false);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md rounded-2xl border bg-white p-8 shadow-xl">
        <div className="mb-6 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
            <KeyRound aria-hidden="true" />
          </div>
          <h1 className="mt-5 text-2xl font-bold">Create your private password</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Your temporary credential has expired. Set a new password before accessing the system.
          </p>
        </div>

        {error && (
          <div className="mb-4 flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <AlertCircle className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <PasswordField
            label="New password"
            value={form.password}
            onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
            visible={showPassword}
            onToggle={() => setShowPassword((visible) => !visible)}
            autoComplete="new-password"
          />
          <PasswordField
            label="Confirm new password"
            value={form.confirm}
            onChange={(event) => setForm((current) => ({ ...current, confirm: event.target.value }))}
            visible={showConfirm}
            onToggle={() => setShowConfirm((visible) => !visible)}
            autoComplete="new-password"
          />
          <p className="text-xs text-muted-foreground">
            Use at least 10 characters and avoid your name, PF number or registration number.
          </p>
          <Button className="w-full" type="submit" disabled={saving}>
            {saving ? "Updating password…" : "Set password and continue"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
